import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import ApiService from '../../services/apiService';
import {
    LessFreightRecommendation,
    MoreFreightRecommendation,
    NetworkRecommendations,
    Recommendation,
    StartServiceRecommendation,
} from '../../interfaces/services/networkRecommendations';
import { Lane, LanesResponse } from '../../interfaces/services/lanes';
import { ApiStatus, RecommendationType } from '../../utils/enums';
import { getTTCBaseUrl } from '../../utils/environmentUtils';
import { formatNetworkRecommendationsFilters } from '../../utils/stateHelpers';

interface NetworkRxState {
    selectedRecommendationType: RecommendationType;
    selectedRecommendations: (Recommendation | MoreFreightRecommendation | LessFreightRecommendation | StartServiceRecommendation)[];
    networkRecommendationsApiStatus: ApiStatus;
    networkRecommendations: NetworkRecommendations;
    lanesApiStatus: ApiStatus;
    lanes: Lane[];
}

const initialState: NetworkRxState = {
    selectedRecommendationType: RecommendationType.MoveMoreFreight,
    selectedRecommendations: [],
    networkRecommendationsApiStatus: ApiStatus.Idle,
    networkRecommendations: {
        network_health: {
            network_grade: null,
            network_gpa: null,
            network_stats: {
                average_daily_balance_amount: null,
                average_daily_density_amount: null,
                empty_miles_percentage_amount: null,
                average_scheduled_vs_actual_miles_per_hour: null,
                average_load_hours_duration: null,
                average_unload_hours_duration: null,
                average_netwise_in_transit_delay_hours_ratio: null,
            },
        },
        recommendation_count_details: {
            start_service: 0,
            stop_service: 0,
            move_more_freight: 0,
            move_less_freight: 0,
        },
        recommendations: {
            move_less_freight: [],
            move_more_freight: [],
            start_service: [],
            stop_service: [],
        },
    },
    lanesApiStatus: ApiStatus.Idle,
    lanes: [],
};

export const fetchNetworkRecommendations = createAsyncThunk(
    'networkRx/fetchNetworkRecommendations',
    async () => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v1/insights/network/recommendations/lanes`,
                body: {
                    filter_criteria: formatNetworkRecommendationsFilters(),
                },
            }) as AxiosResponse<NetworkRecommendations>;

            return data;
        } catch (error) {
            console.error('Unable to fetch network recommendations', error);
            toast.error('Unable to fetch network recommendations');
            throw error;
        }
    }
);

export const fetchLanes = createAsyncThunk(
    'lanes/fetchLanes',
    async () => {
        try {
            const networkRecommendationFilters = formatNetworkRecommendationsFilters();
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/lanes`,
                body: {
                    pickup_service_area_id: null,
                    delivery_service_area_id: null,
                    filter_criteria: {
                        earliest_delivery_departure_timestamp: networkRecommendationFilters.historical_start_date,
                        latest_delivery_departure_timestamp: networkRecommendationFilters.historical_end_date,
                        cleanse_data: networkRecommendationFilters.cleanse_data,
                    },
                },
            }) as AxiosResponse<LanesResponse>;

            return data;
        } catch (error) {
            console.error('Unable to load lanes', error);
            toast.error('Unable to load lanes');
            throw error;
        }
    }
);

export const networkRxSlice = createSlice({
    name: 'networkRx',
    initialState,
    reducers: {
        updateSelectedRecommendationType: (state, action: PayloadAction<RecommendationType>) => {
            state.selectedRecommendationType = action.payload;
            state.selectedRecommendations = [];
        },
        updateSelectedRecommendations: (state, action: PayloadAction<(Recommendation | MoreFreightRecommendation | LessFreightRecommendation)[]>) => {
            state.selectedRecommendations = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchNetworkRecommendations.pending, (state) => {
            state.networkRecommendationsApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchNetworkRecommendations.fulfilled, (state, action) => {
            state.networkRecommendationsApiStatus = ApiStatus.Success;
            state.networkRecommendations = action.payload;
        });
        builder.addCase(fetchNetworkRecommendations.rejected, (state) => {
            state.networkRecommendationsApiStatus = ApiStatus.Failure;
            state.networkRecommendations = initialState.networkRecommendations;
        });
        builder.addCase(fetchLanes.pending, (state) => {
            state.lanesApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchLanes.fulfilled, (state, action) => {
            state.lanesApiStatus = ApiStatus.Success;
            state.lanes = action.payload.lanes;
        });
        builder.addCase(fetchLanes.rejected, (state) => {
            state.lanesApiStatus = ApiStatus.Failure;
            state.lanes = [];
        });
    },
});

export const {
    updateSelectedRecommendationType,
    updateSelectedRecommendations,
} = networkRxSlice.actions;
export default networkRxSlice.reducer;
